// Cross-subdomain cookie storage for the 18+ confirmation.
//
// When the page is served from any `*.hypneuronerd.ing` host, the cookie is
// written with `Domain=.hypneuronerd.ing` so it's visible across every
// subdomain. When served from anywhere else (localhost, Cloudflare preview
// URLs like `*.pages.dev`), the cookie is scoped to the current host as a
// fallback — better than nothing, even if confirmation doesn't carry.

const PARENT_DOMAIN = 'hypneuronerd.ing';
const COOKIE_NAME = 'hnn_age';
const DEV_BYPASS_COOKIE = 'hnn_dev_bypass';
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

function isOnParentDomain(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === PARENT_DOMAIN || host.endsWith(`.${PARENT_DOMAIN}`);
}

function isSecure(): boolean {
  return typeof window !== 'undefined' && window.location.protocol === 'https:';
}

function buildAttributes(): string {
  const parts: string[] = [];
  if (isOnParentDomain()) {
    // Leading dot is the historical convention and broadly supported.
    parts.push(`Domain=.${PARENT_DOMAIN}`);
  }
  parts.push('Path=/');
  parts.push(`Max-Age=${MAX_AGE_SECONDS}`);
  parts.push('SameSite=Lax');
  if (isSecure()) parts.push('Secure');
  return parts.join('; ');
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const target = `${name}=`;
  const cookies = document.cookie.split(';');
  for (const c of cookies) {
    const trimmed = c.trim();
    if (trimmed.startsWith(target)) return decodeURIComponent(trimmed.slice(target.length));
  }
  return null;
}

function writeCookie(name: string, value: string) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; ${buildAttributes()}`;
}

function deleteCookie(name: string) {
  if (typeof document === 'undefined') return;
  // Delete in both possible scopes (parent domain + host-only) so we cover
  // anything written by earlier visits.
  const parts: string[] = ['Path=/', 'Max-Age=0', 'SameSite=Lax'];
  if (isSecure()) parts.push('Secure');
  // Host-only delete
  document.cookie = `${name}=; ${parts.join('; ')}`;
  // Parent-domain delete (only meaningful when on parent domain)
  if (isOnParentDomain()) {
    document.cookie = `${name}=; Domain=.${PARENT_DOMAIN}; ${parts.join('; ')}`;
  }
}

export function getAgeCookie(): boolean {
  return readCookie(COOKIE_NAME) === '1';
}

export function setAgeCookie(value: boolean): void {
  if (value) writeCookie(COOKIE_NAME, '1');
  else deleteCookie(COOKIE_NAME);
}

export function getDevBypassCookie(): boolean {
  return readCookie(DEV_BYPASS_COOKIE) === '1';
}

export function clearAgeCookies(): void {
  deleteCookie(COOKIE_NAME);
  deleteCookie(DEV_BYPASS_COOKIE);
}
